import axios, { AxiosError } from "axios";
import baseUrl from "../../../constants/baseurl";

// the config entry ends in /api/; the helpers below add their own /api/ prefix
export const RETAIL_BASE_URL: string = (baseUrl.retail ?? "https://retail.cilpron.com/api/").replace(/\/api\/?$/, "");

/**
 * The one place that knows where the logged-in user's token is kept.
 * Change the key (or the storage) here if your app stores it somewhere else.
 */
export const getAccessToken = (): string | null => sessionStorage.getItem("access_token");

export const api = axios.create({
  baseURL: RETAIL_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// the token is read on every request, so a refreshed token is picked up straight away
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function errorMessage(e: unknown): string {
  const ax = e as AxiosError<{ title?: string; detail?: string }>;
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? ax.message ?? "Something went wrong.";
}

const post = <T>(url: string, body?: unknown) => api.post<T>(url, body ?? {}).then((r) => r.data);

/* ---------------------------------------------------------------- generic CRUD (every [CrudResource] entity) */

export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

/** page, pageSize, search, sort ("-createdAtUtc" for descending), plus any property name as a filter; add From / To to a property name for ranges. */
export type Query = Record<string, string | number | boolean | undefined>;

export function crud<T extends { id: string }>(route: string) {
  const base = `/api/${route}`;
  return {
    list: (params?: Query) => api.get<Paged<T>>(base, { params }).then((r) => r.data),
    get: (id: string) => api.get<T>(`${base}/${id}`).then((r) => r.data),
    create: (body: Partial<T>) => api.post<T>(base, body).then((r) => r.data),
    update: (id: string, body: Partial<T> & { rowVersion?: string }) => api.put<T>(`${base}/${id}`, body).then((r) => r.data),
    remove: (id: string) => api.delete(`${base}/${id}`).then(() => undefined),
  };
}

/* ---------------------------------------------------------------- dashboard (/api/dashboard/*) */

export interface HourSales { hour: number; netSales: number; orders: number }
export interface NamedAmount { code: string; name: string; amount: number; count: number }
export interface AttentionItem { kind: string; severity: string; title: string; detail?: string | null; entityId?: string | null }
export interface TopProduct { productId: string; sku: string; description: string; units: number; revenue: number; margin: number }

export interface DashboardOverview {
  storeId?: string | null;
  businessDate: string;
  currencyCode: string;
  netSales: number;
  netSalesYesterday: number;
  netSalesChangePct: number;
  orders: number;
  averageOrder: number;
  openShifts: number;
  openBoardOrders: number;
  lowStockItems: number;
  byHour: HourSales[];
  topSellers: TopProduct[];
  paymentMethods: NamedAmount[];
  needsAttention: AttentionItem[];
  asOfUtc: string;
}

export interface DayAmount { date: string; netSales: number; orders: number }
export interface SalesReport {
  storeId?: string | null;
  from: string;
  to: string;
  currencyCode: string;
  netSales: number;
  grossSales: number;
  discounts: number;
  tax: number;
  orders: number;
  averageOrder: number;
  daily: DayAmount[];
  byCategory: NamedAmount[];
  byPaymentMethod: NamedAmount[];
  byCashier: NamedAmount[];
  bestSellers: TopProduct[];
}

export interface OrderBoardCard {
  orderId: string;
  fulfillmentId?: string | null;
  orderNo: string;
  customerName: string;
  itemCount: number;
  placedAtUtc: string;
  minutesAgo: number;
  place?: string | null;
  fulfillmentType: string;
  status: string;
  statusLabelKey: string;
  pickupCode?: string | null;
  storeId?: string | null;
}

export const dashboard = {
  overview: (storeId?: string) => api.get<DashboardOverview>("/api/dashboard/overview", { params: { storeId } }).then((r) => r.data),
  salesReport: (from: string, to: string, storeId?: string) => api.get<SalesReport>("/api/dashboard/sales-report", { params: { from, to, storeId } }).then((r) => r.data),
  orderBoard: (storeId?: string, take = 50) => api.get<OrderBoardCard[]>("/api/dashboard/order-board", { params: { storeId, take } }).then((r) => r.data),
};

/* ---------------------------------------------------------------- point of sale (/api/pos/sale/*) */

export interface SaleLineDto {
  id: string;
  lineNo: number;
  productId: string;
  sku: string;
  description: string;
  quantity: number;
  unitPrice: number;
  extendedPrice: number;
  discountAmount: number;
  taxAmount: number;
  lineTotal: number;
  status: string;
}
export interface SalePaymentDto { id: string; tenderCode: string; amount: number; tenderedAmount: number; changeGiven: number; status: string }
export interface SaleDiscountDto { id: string; source: string; description: string; amount: number; couponCode?: string | null }
export interface SaleDto {
  id: string;
  receiptNo: string;
  storeId: string;
  registerId: string;
  customerId?: string | null;
  status: string;
  currencyCode: string;
  taxInclusive: boolean;
  subTotal: number;
  discountTotal: number;
  taxTotal: number;
  roundingAdjustment: number;
  total: number;
  amountPaid: number;
  changeGiven: number;
  couponCodesCsv?: string | null;
  startedAtUtc: string;
  completedAtUtc?: string | null;
  lines: SaleLineDto[];
  payments: SalePaymentDto[];
  discounts: SaleDiscountDto[];
}
export interface PaymentInput {
  tenderCode: string;
  amount?: number;
  tenderedAmount?: number;
  tenderCurrency?: string;
  reference?: string;
  storedValueNumber?: string;
  pin?: string;
  loyaltyPoints?: number;
  externalAuthCode?: string;
  externalReference?: string;
}
export interface PaymentOutcome { saleId: string; paymentId: string; applied: number; changeGiven: number; remaining: number; fullyPaid: boolean }

const POS = "/api/pos/sale";
export const saleApi = {
  start: (b: { registerId: string; customerId?: string | null; associateId?: string | null; culture?: string }) => post<SaleDto>(`${POS}/start`, b),
  get: (id: string) => api.get<SaleDto>(`${POS}/${id}`).then((r) => r.data),
  addItem: (id: string, b: { productId?: string; code?: string; quantity: number }) => post<SaleDto>(`${POS}/${id}/add-item`, b),
  setQuantity: (id: string, lineId: string, quantity: number) => post<SaleDto>(`${POS}/${id}/${lineId}/set-quantity`, { quantity }),
  voidLine: (id: string, lineId: string, reason?: string) => post<SaleDto>(`${POS}/${id}/${lineId}/void-line`, { reason }),
  addCoupon: (id: string, code: string) => post<SaleDto>(`${POS}/${id}/add-coupon`, { code }),
  removeCoupon: (id: string, code: string) => post<SaleDto>(`${POS}/${id}/remove-coupon`, { code }),
  attachCustomer: (id: string, customerId: string | null) => post<SaleDto>(`${POS}/${id}/attach-customer`, { customerId }),
  suspend: (id: string, reason?: string) => post<SaleDto>(`${POS}/${id}/suspend`, { reason }),
  resume: (id: string) => post<SaleDto>(`${POS}/${id}/resume`),
  addPayment: (id: string, b: PaymentInput) => post<PaymentOutcome>(`${POS}/${id}/add-payment`, b),
  complete: (id: string) => post<SaleDto>(`${POS}/${id}/complete`),
  void: (id: string, reason: string) => post<SaleDto>(`${POS}/${id}/void`, { reason }),
};

/* ---------------------------------------------------------------- cash drawer (/api/cash/*) */

export const cashApi = {
  openTill: (b: { registerId: string; associateId: string; openingFloat: number; blindClose?: boolean }) => post<{ id: string }>("/api/cash/open-till", b),
  closeTill: (tillId: string, counts: { tenderCode: string; currencyCode: string; countedAmount: number }[]) => post<{ id: string }>(`/api/cash/${tillId}/close-till`, { counts }),
};

/* ---------------------------------------------------------------- online orders and fulfilment (/api/orders/order/*) */

const ORD = "/api/orders/order";
export const orderApi = {
  startPicking: (fulfillmentId: string, associateId?: string | null) => post(`${ORD}/${fulfillmentId}/start-picking`, { associateId: associateId ?? null }),
  markPacked: (fulfillmentId: string) => post(`${ORD}/${fulfillmentId}/mark-packed`),
  markReady: (fulfillmentId: string) => post(`${ORD}/${fulfillmentId}/mark-ready-for-pickup`),
  handOver: (fulfillmentId: string, pickupCode?: string | null) => post(`${ORD}/${fulfillmentId}/hand-over`, { pickupCode: pickupCode ?? null }),
  ship: (fulfillmentId: string) => post(`${ORD}/${fulfillmentId}/ship`, { shippingCost: 0 }),
  cancel: (orderId: string, reason?: string) => post(`${ORD}/${orderId}/cancel`, { reason }),
};